import BaseIntegration from '@/common/mixins/integrations/baseIntegration';
import { mapMutations, mapActions, mapGetters } from 'vuex';

/*
 * OP014586-4 — Duel Vodafone-Oman (DV Pass) integration mixin.
 *
 * Duel uses a per-operator mixin architecture (this folder + the main.js factory), whereas the
 * reference implementation on Brainiac keeps every operator in one monolithic integration.js with big
 * switch blocks. This file is the reshape of Brainiac's four `case VODAFONE_OMAN:` blocks into Duel's
 * `...Impl` contract (see baseIntegration.js / integrationControll.js):
 *   - getAlternateRequestPinActionImpl  <- integration.js:499  (in-app request pin + DVPass identification)
 *   - getAlternateVerifyPinActionImpl   <- integration.js:974  (in-app verify pin, async getStatus poll)
 *   - alternateReSubActionImpl          <- integration.js:1127 (reopt-in)
 * The re-sub is routed here by the `hasHeReSub` flag on the VODAFONE_OMAN config entry, because Duel's
 * getAppropriateReSubAction dispatches by flag, not by a per-operator case.
 *
 * HE (header enrichment) is dormant: getDvPassIdentificationId is only populated when a network-detected
 * landing ran the DVPass identification call, so with no HE landing the identificationId is simply null.
 */
const VodafoneOmanIntegrationMixin = {
  extends: BaseIntegration,
  computed: {
    ...mapGetters('common', [
      'getDvPassIdentificationId',
      'getDailySubscription',
      'getDVPassChallenge',
    ]),
  },
  methods: {
    ...mapActions('profile', [
      'storeLoginInfo',
      'getUserLoginDetails',
    ]),
    ...mapActions('i18n', [
      'updateUserLanguage',
    ]),
    ...mapMutations('common', [
      'setDVPassJWT',
    ]),
    ...mapMutations('profile', [
      'setRegistrationInfo',
    ]),
    getAlternateRequestPinActionImpl(isDailySubscription) {
      return () => {
        this.message = '';
        this.waitForResponse = true;
        const path = `api/requestPinInApp?msisdn=${this.user.msisdn}&lang=${this.getLocale}&isDailySubscription=${isDailySubscription}&identificationId=${this.getDvPassIdentificationId}`;
        this.$axios.get(path).then((response) => {
          const resp = response.data.responseEnum;
          if (resp === 'OPERATION_SUCCESS') {
            this.setRegistrationInfo(this.user.msisdn);
            sessionStorage.setItem('msisdn', this.user.msisdn);
            this.user.msisdn = '';
            // Remove "Bearer " prefix before storing the DVPass JWT
            const jwt = response.data.data.replace('Bearer ', '');
            this.setDVPassJWT(jwt);
            this.$nextTick(() => {
              this.$router.push('register2');
            });
            this.waitForResponse = false;
          } else if (resp === 'ALREADY_SUB_USER') {
            this.mapErrorMessages('ALREADY_REGISTERED');
            this.hasErrors = true;
            this.waitForResponse = false;
          } else {
            this.mapErrorMessages(resp);
            this.hasErrors = true;
            this.waitForResponse = false;
          }
        }).catch((error) => {
          // OP014586-4 — the port carried over no error handling: a network failure or 5xx left the
          // spinner spinning with nothing logged, indistinguishable from a slow core. Log it and
          // release the UI so the failure is observable during testing and in production.
          this.waitForResponse = false;
          this.hasErrors = true;
          // eslint-disable-next-line no-console
          console.error('VodafoneOman : requestPinInApp failed', error);
        });
      };
    },
    // The port called showErrorMessage but never defined it (Brainiac leaves it undefined too), so every
    // non-success verify threw a TypeError and the user was left with no message at all.
    showErrorMessage(responseEnum) {
      const messages = {
        PIN_EXPIRED: 'visitor_sign_up_2_pin_expired',
        WRONG_PIN: 'visitor_sign_up_2_warning_msg_wrong_verification_code',
        BLOCKED_USER: 'visitor_forgot_password_warning_msd_max_attempts',
        SERVER_ERROR: 'home_profile_profile_edit_server_unvailable',
        THIRD_PARTY_PIN_REQUEST_FAILED: 'visitor_sign_up_warning_third_party_error',
        THIRD_PARTY_PIN_VERIFICATION_FAILED: 'visitor_sign_up_warning_third_party_error',
        THIRD_PARTY_SUBSCRIPTION_PIN_REQUEST_FAILED: 'visitor_sign_up_warning_third_party_error',
        TEST_MSISDN_ACTION_PERFOMED: 'registration_process_with_test_msisdn_perfomed',
      };
      // Unknown enums fall back to the generic server message rather than staying silent.
      this.message = messages[responseEnum] || 'home_profile_profile_edit_server_unvailable';
      this.hasErrors = true;
      this.waitForResponse = false;
    },
    getAlternateVerifyPinActionImpl() {
      return () => {
        this.$axios.get(`api/verifyPinInApp?msisdn=${this.registrationInfoMsisdn}&pin=${this.user.pin}&lang=${this.getLocale}&clickid=${this.getClickId || ''}&refUrl=${this.getRefUrl || ''}&publisherId=${this.getPublisherId || ''}&campaignId=${this.getCampaignId || ''}&isDailySubscription=${this.getDailySubscription}&dvPassChallenge=${this.getDVPassChallenge}&asyncSub=true`).then((response1) => {
          const resp = response1.data.responseEnum;
          this.waitForResponse = false;
          if (resp === 'OPERATION_SUCCESS') { // proceed to authorization
            this.showLoadingModal = true;
            this.$axios.get(`api/getStatus?msisdn=${sessionStorage.getItem('msisdn')}&pin=${this.user.pin}&lang=${this.getLocale}&clickid=${this.getClickId || ''}&refUrl=${this.getRefUrl || ''}&publisherId=${this.getPublisherId || ''}&campaignId=${this.getCampaignId || ''}`).then((res) => {
              this.showLoadingModal = false;
              if (res.data.responseEnum === 'OPERATION_SUCCESS' && res.data.data) {
                const hasSub = res.data.data.isSub;
                if (hasSub === 'true') {
                  this.sendCompleteRegistrationEvent();
                }
                this.storeLoginInfo({
                  token: res.data.data.user_auth_token,
                  rememberMeParameter: false,
                  username: res.data.data.username,
                });
                this.getUserLoginDetails().then(() => {
                  this.updateUserLanguage();
                  this.$router.push('home');
                });
              } else {
                if (resp === 'PIN_EXPIRED') {
                  this.message = 'visitor_sign_up_2_pin_expired';
                } else if (resp === 'WRONG_PIN') {
                  this.message = 'visitor_sign_up_2_warning_msg_wrong_verification_code';
                } else if (resp === 'BLOCKED_USER') {
                  this.message = 'visitor_forgot_password_warning_msd_max_attempts';
                } else if (resp === 'SERVER_ERROR') {
                  this.message = 'home_profile_profile_edit_server_unvailable';
                } else if ((resp === 'THIRD_PARTY_PIN_REQUEST_FAILED') || (resp === 'THIRD_PARTY_PIN_VERIFICATION_FAILED') || (resp === 'THIRD_PARTY_SUBSCRIPTION_PIN_REQUEST_FAILED')) {
                  this.message = 'visitor_sign_up_warning_third_party_error';
                } else if (resp === 'TEST_MSISDN_ACTION_PERFOMED') {
                  this.message = 'registration_process_with_test_msisdn_perfomed';
                } else { // error case
                  this.$router.push('/');
                }
                this.showLoadingModal = false;
                this.hasErrors = true;
              }
            }).catch((error) => {
              // The async getStatus poll is the step most likely to hang; without this the loading
              // modal stays up forever and the console says nothing.
              this.showLoadingModal = false;
              this.hasErrors = true;
              // eslint-disable-next-line no-console
              console.error('VodafoneOman : getStatus poll failed', error);
            });
          } else if (response1.data === 'OLD_PASSWORD_CHANGED') { // here user's password updated
            this.authorizationProcess();
          } else {
            this.showErrorMessage(resp);
          }
        }).catch((error) => {
          this.waitForResponse = false;
          this.showLoadingModal = false;
          this.hasErrors = true;
          // eslint-disable-next-line no-console
          console.error('VodafoneOman : verifyPinInApp failed', error);
        });
      };
    },
    alternateReSubActionImpl() {
      return (msisdn) => {
        this.$axios.get(`api/requestPinInApp?msisdn=${msisdn}&reoptin=true&lang=${this.getLocale}&isDailySubscription=true`).then((response) => {
          this.reSubCallback(response); // implemented by the consuming component
        }).catch((error) => {
          // eslint-disable-next-line no-console
          console.error('VodafoneOman : reoptin requestPinInApp failed', error);
        });
      };
    },
  },
};

export default VodafoneOmanIntegrationMixin;



// WEBPACK FOOTER //
// ./src/common/mixins/integrations/vodafoneOmanIntegration.js